import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { 
  CheckCircleIcon, 
  ExclamationTriangleIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import { useAuthStore } from '../../stores/authStore';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

interface LawsuitJoinData {
  id: string;
  title: string;
  description: string;
  category: string;
  targetEntity: string;
  costPerParticipant: number;
  participants: number;
  maxParticipants: number;
  minimumParticipants: number;
  status: string;
  terms: string[];
}

const JoinLawsuitPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const [lawsuit, setLawsuit] = useState<LawsuitJoinData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isJoining, setIsJoining] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchLawsuitData = async () => {
      try {
        setIsLoading(true);
        // TODO: Replace with actual API call
        // const response = await axios.get(`/api/lawsuits/${id}/join-info`);
        // setLawsuit(response.data.data.lawsuit);
        
        // Mock data for now
        const mockLawsuit: LawsuitJoinData = {
          id: id || '1',
          title: 'Class Action Against Tech Corp Data Breach',
          description: 'Seeking damages for users affected by the recent data breach.',
          category: 'Data Privacy',
          targetEntity: 'Tech Corp Inc.',
          costPerParticipant: 500,
          participants: 45,
          maxParticipants: 1000,
          minimumParticipants: 50,
          status: 'active',
          terms: [
            'I understand that by joining this lawsuit, I agree to be represented by the assigned legal team.',
            'I acknowledge that legal proceedings can take time and outcomes are not guaranteed.',
            'I agree to pay the specified participation fee to cover legal costs.',
            'I understand that any settlement or judgment will be distributed among participants.',
            'I consent to sharing necessary information with the legal team for the case.'
          ]
        };
        
        setLawsuit(mockLawsuit);
      } catch (err) {
        console.error('Failed to fetch lawsuit join data:', err);
        setError(t('errors.failedToLoad', 'Failed to load lawsuit information'));
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchLawsuitData();
    }
  }, [id, t]);

  const handleJoinConfirm = async () => {
    if (!lawsuit || !user || !acceptedTerms) return;

    try {
      setIsJoining(true);
      // TODO: Replace with actual API call to join lawsuit
      // await axios.post(`/api/lawsuits/${lawsuit.id}/join`);
      
      // Navigate to payment page
      navigate(`/payment/${lawsuit.id}`);
    } catch (err) {
      console.error('Failed to join lawsuit:', err);
      setError(t('lawsuit.joinError', 'Failed to join lawsuit'));
    } finally {
      setIsJoining(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !lawsuit) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-500" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {error || t('errors.lawsuitNotFound', 'Lawsuit not found')}
          </h3>
          <div className="mt-6">
            <button
              onClick={() => navigate('/browse-lawsuits')}
              className="inline-flex items-center px-4 py-3 sm:py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-cyan-600 hover:bg-cyan-700 min-h-[44px]"
            >
              {t('lawsuit.browseLawsuits', 'Browse Lawsuits')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isEligible = lawsuit.status === 'active' && lawsuit.participants < lawsuit.maxParticipants;

  return (
    <>
      <Helmet>
        <title>{t('lawsuit.joinLawsuit', 'Join Lawsuit')} - {lawsuit.title} - tvia.ai</title>
        <meta name="description" content={`Join the lawsuit: ${lawsuit.title}`} />
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
              {t('lawsuit.joinLawsuit', 'Join Lawsuit')}
            </h1>
            <p className="mt-2 text-base sm:text-lg text-gray-600">
              {t('lawsuit.reviewAndConfirm', 'Review the details below and confirm your participation')}
            </p>
          </div>

          {/* Lawsuit Summary */}
          <div className="bg-white shadow rounded-lg mb-6 sm:mb-8">
            <div className="px-4 sm:px-6 py-6 sm:py-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
                {lawsuit.title}
              </h2>
              <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
                {lawsuit.description}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
                <div className="flex items-center">
                  <DocumentTextIcon className="h-5 w-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      {t('lawsuit.category', 'Category')}
                    </p>
                    <p className="text-sm text-gray-900">{lawsuit.category}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <ExclamationTriangleIcon className="h-5 w-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      {t('lawsuit.targetEntity', 'Target Entity')}
                    </p>
                    <p className="text-sm text-gray-900">{lawsuit.targetEntity}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <UserGroupIcon className="h-5 w-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      {t('lawsuit.participants', 'Participants')}
                    </p>
                    <p className="text-sm text-gray-900">
                      {lawsuit.participants} / {lawsuit.maxParticipants}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Cost Breakdown */}
          <div className="bg-white shadow rounded-lg mb-6 sm:mb-8">
            <div className="px-4 sm:px-6 py-6 sm:py-8">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">
                {t('lawsuit.costBreakdown', 'Cost Breakdown')}
              </h3>
              <div className="bg-gray-50 rounded-lg p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-start sm:items-center">
                    <CurrencyDollarIcon className="h-5 w-5 sm:h-6 sm:w-6 text-cyan-600 mr-2 sm:mr-3 flex-shrink-0 mt-1 sm:mt-0" />
                    <div>
                      <h4 className="text-base sm:text-lg font-medium text-gray-900">
                        {t('lawsuit.participationFee', 'Participation Fee')}
                      </h4>
                      <p className="text-xs sm:text-sm text-gray-500">
                        {t('lawsuit.feeDescription', 'This fee covers legal costs and administrative expenses')}
                      </p>
                    </div>
                  </div>
                  <div className="text-left sm:text-right w-full sm:w-auto">
                    <p className="text-2xl sm:text-3xl font-bold text-cyan-600">
                      ${lawsuit.costPerParticipant}
                    </p>
                    <p className="text-xs sm:text-sm text-gray-500">
                      {t('lawsuit.oneTimeFee', 'One-time fee')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Terms and Conditions */}
          <div className="bg-white shadow rounded-lg mb-6 sm:mb-8">
            <div className="px-4 sm:px-6 py-6 sm:py-8">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">
                {t('lawsuit.termsAndConditions', 'Terms and Conditions')}
              </h3>
              <div className="space-y-4">
                {lawsuit.terms.map((term, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-gray-700">{term}</p>
                  </div>
                ))}
              </div>
              
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-center">
                  <input
                    id="acceptTerms"
                    name="acceptTerms"
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
                  />
                  <label htmlFor="acceptTerms" className="ml-2 block text-sm text-gray-900">
                    {t('lawsuit.acceptTermsConfirm', 'I have read and accept the terms and conditions above')}
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Eligibility Check */}
          {!isEligible && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-8">
              <div className="flex">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mr-3" />
                <div>
                  <h3 className="text-sm font-medium text-yellow-800">
                    {t('lawsuit.notEligible', 'Not Eligible to Join')}
                  </h3>
                  <p className="mt-1 text-sm text-yellow-700">
                    {lawsuit.status !== 'active' 
                      ? t('lawsuit.lawsuitNotActive', 'This lawsuit is no longer accepting new participants.')
                      : t('lawsuit.lawsuitFull', 'This lawsuit has reached its maximum number of participants.')
                    }
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row sm:justify-between gap-3 sm:gap-4">
            <button
              onClick={() => navigate(`/lawsuit/${lawsuit.id}`)}
              className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 shadow-sm text-sm sm:text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 min-h-[44px]"
            >
              {t('common.goBack', 'Go Back')}
            </button>

            <button
              onClick={handleJoinConfirm}
              disabled={!isEligible || !acceptedTerms || isJoining}
              className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-sm sm:text-base font-medium rounded-md text-white bg-cyan-600 hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
            >
              {isJoining ? (
                <LoadingSpinner size="sm" />
              ) : (
                <>
                  <CurrencyDollarIcon className="h-5 w-5 mr-2" />
                  {t('lawsuit.proceedToPayment', 'Proceed to Payment')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default JoinLawsuitPage; 